import React from 'react';
import PropTypes from 'prop-types';
import CrossIcon from 'mdi-react/CloseIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import { bemGenerator, noop } from '@prisjakt/ui-base';
import './popover.scss';

const styles = bemGenerator('pj-ui-popover', [
  'wrapper',
  'content',
  'arrow',
  'arrow-container',
  'close',
]);

class PopOver extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      visuallyHidden: false,
      currentWidth: null,
    };

    this.clickEvent = e => this.handleOutSideClick(e);
    this.resizeEvent = () => this.handleBreakPoints();
  }

  componentDidMount() {
    const { position } = this.props;

    if (position === 'bottom-responsive') {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({
        visuallyHidden: true,
        currentWidth: this.calculateCurrentWidth(),
      });
    }
  }

  componentWillUnmount() {
    const { breakpoints } = this.props;

    if (breakpoints) {
      window.removeEventListener('resize', this.resizeEvent);
    }

    this.toggleClickListener(false);
  }

  toggleClickListener(visible) {
    if (visible) {
      window.addEventListener('click', this.clickEvent);

      if (this.props.breakpoints) {
        window.addEventListener('resize', this.resizeEvent);
      }
    } else {
      window.removeEventListener('click', this.clickEvent);
      window.removeEventListener('resize', this.resizeEvent);
    }
  }

  componentDidUpdate() {
    if (this.arrow) {
      const { top, left, display } = this.getArrowStyle();
      this.arrow.style.left = left;
      this.arrow.style.top = top;
      this.arrow.style.display = display;
    }

    if (this.contentWrapper) {
      const {
        top,
        left,
        right,
        width,
        marginTop,
        zIndex,
      } = this.getContentStyle();
      this.contentWrapper.style.top = top;
      this.contentWrapper.style.left = left;
      this.contentWrapper.style.right = right;
      this.contentWrapper.style.width = width;
      this.contentWrapper.style.marginTop = marginTop;
      this.contentWrapper.style.zIndex = zIndex;
    }
  }

  handleKeyDown(e) {
    const [ENTER, SPACE, ESCAPE] = [13, 32, 27];
    const { visible } = this.state;

    switch (e.keyCode) {
      case ENTER:
      case SPACE:
        if (visible) {
          this.hide();
        } else {
          this.show();
        }
        break;
      case ESCAPE:
        this.hide();
        break;
      default:
      // do nothing
    }
  }

  handleOutSideClick(e) {
    const visible = this.getVisibilityValue();

    if (
      !visible ||
      this.contentWrapper.contains(e.target) ||
      this.outerWrapper.contains(e.target)
    ) {
      return;
    }

    this.hide();
  }

  calculateCurrentWidth() {
    const { innerWidth } = window;
    const { breakpoints } = this.props;

    if (!breakpoints) {
      return this.props.width;
    }

    let newWidth = this.props.width; // fallback to default
    if (breakpoints.medium && innerWidth < breakpoints.medium.point) {
      newWidth = breakpoints.medium.width;
    }

    if (breakpoints.small && innerWidth < breakpoints.small.point) {
      newWidth = breakpoints.small.width;
    }

    return newWidth;
  }

  handleBreakPoints() {
    const { breakpoints } = this.props;

    if (!breakpoints) {
      return;
    }

    // Content wrapper needs to be mounted
    // uses currentWidth in style calculations
    if (this.contentWrapper) {
      this.setState({
        currentWidth: this.calculateCurrentWidth(),
      });
    }
  }

  getContentStyle() {
    const { position, width, offsetX, offsetY } = this.props;
    const currentWidth = this.state.currentWidth || width;

    if (!this.outerWrapper) {
      return null;
    }

    const { clientWidth: wrapperWidth } = this.outerWrapper;
    let leftOffset;

    switch (position) {
      case 'left':
      case 'right': {
        const unit = 'px';
        return {
          [position]: -currentWidth + offsetX + unit,
          width: currentWidth + unit,
          top: offsetY + unit,
        };
      }
      case 'top': {
        // Center the popover box below the wrapped element
        if (wrapperWidth > currentWidth) {
          leftOffset = (wrapperWidth - currentWidth) / 2;
        } else {
          leftOffset = -(currentWidth - wrapperWidth) / 2;
        }
        leftOffset += offsetX;

        const { clientHeight: contentHeight } = this.contentWrapper;

        return {
          left: `${leftOffset}px`,
          top: `${offsetY - contentHeight}px`,
          width: `${this.calculateCurrentWidth()}px`,
          zIndex: 4,
        };
      }
      case 'bottom-responsive': {
        // Adjust popover box to the left or right side of the closest relative positioned parent
        const rect = this.outerWrapper.getBoundingClientRect();
        const halfWindow = window.innerWidth / 2;

        leftOffset =
          rect.right > halfWindow ? 100 - this.state.currentWidth : 0;

        return {
          left: `calc(${leftOffset}% + ${offsetX}px)`,
          top: 0,
          marginTop: `${offsetY}px`,
          width: `${currentWidth}%`,
        };
      }
      case 'bottom':
      default: {
        // Center the popover box below the wrapped element
        const unit = 'px';
        if (wrapperWidth > currentWidth) {
          leftOffset = (wrapperWidth - currentWidth) / 2;
        } else {
          leftOffset = -(currentWidth - wrapperWidth) / 2;
        }
        leftOffset += offsetX;

        return {
          left: leftOffset + unit,
          marginTop: offsetY + unit,
          width: currentWidth + unit,
        };
      }
    }
  }

  getArrowStyle() {
    const { position, arrow, arrowOffsetX, arrowOffsetY } = this.props;
    const unit = 'px';

    if (!arrow) {
      return { display: 'none' };
    }

    switch (position) {
      case 'left':
      case 'right':
        return { top: arrowOffsetY + unit, display: 'block' };
      case 'top': {
        const offsetX = 16;
        return {
          left: arrowOffsetX + offsetX + unit,
          display: 'block',
        };
      }
      case 'bottom-responsive': {
        const arrowWidth = 8;
        const minOffset = 10;

        const offsetX =
          this.outerWrapper.offsetLeft +
          this.outerWrapper.offsetWidth / 2 -
          (this.contentWrapper.offsetLeft + arrowWidth);

        return {
          left: offsetX > 0 ? offsetX + unit : minOffset + unit,
          display: 'block',
        };
      }
      case 'bottom':
      default:
        return { left: arrowOffsetX + unit, display: 'block' };
    }
  }

  show(e) {
    if (this.state.visible && this.contentWrapper.contains(e.target)) {
      return;
    }

    this.toggleClickListener(true);

    this.setState(prevState => ({
      visible: !prevState.visible,
      currentWidth: this.calculateCurrentWidth(),
    }));
  }

  hide = () => {
    if (!this.state.visible) {
      return;
    }

    this.toggleClickListener(false);

    this.setState({
      visible: false,
    });
  };

  getVisibilityValue() {
    const { open } = this.props;

    let visible;
    if (open === false || open === true) {
      visible = open;
    } else {
      visible = this.state.visible;
    }

    return visible;
  }

  renderContent() {
    const { t } = this.context;
    const visible = this.getVisibilityValue();
    const { position, onClose } = this.props;
    const [_, ...popoverContent] = this.props.children; // eslint-disable-line no-unused-vars

    return (
      <span
        ref={div => {
          this.contentWrapper = div;
        }}
        className={styles.content.withModifier({
          visible,
          [position]: true,
        })}
      >
        <button
          type="button"
          aria-label={t('symbols', 'symbol.close')}
          onClick={() => {
            this.hide();
            onClose();
          }}
          className={styles.close.withModifier({ [position]: true })}
        >
          <CrossIcon size={16} />
        </button>
        <span
          className={styles['arrow-container'].withModifier({
            [position]: true,
          })}
        >
          <span
            ref={span => {
              this.arrow = span;
            }}
            className={styles.arrow.withModifier({ [position]: true })}
          />
        </span>
        {popoverContent}
      </span>
    );
  }

  renderHidden() {
    const visible = this.getVisibilityValue();

    if (!visible) {
      return null;
    }

    return this.renderContent();
  }

  render() {
    const [popoverElement] = this.props.children;
    const { position, label } = this.props;
    const { visuallyHidden } = this.state;

    return (
      <div
        ref={div => {
          this.outerWrapper = div;
        }}
        tabIndex={0}
        role="button"
        aria-label={label}
        className={styles.wrapper.withModifier({
          relative: position !== 'bottom-responsive',
        })}
        onClick={e => this.show(e)}
        onKeyDown={e => this.handleKeyDown(e)}
      >
        {popoverElement}
        {visuallyHidden ? this.renderContent() : this.renderHidden()}
      </div>
    );
  }
}

const { string, bool, node, number, oneOf, oneOfType, shape, func } = PropTypes;

const breakPointShape = shape({ point: number, width: number });
PopOver.propTypes = {
  children: node.isRequired,
  open: bool,
  label: string,
  position: oneOf(['top', 'bottom', 'left', 'right', 'bottom-responsive']),
  width: number,
  offsetY: number,
  offsetX: number,
  arrow: bool,
  arrowOffsetX: number,
  arrowOffsetY: number,
  breakpoints: oneOfType([
    shape({ medium: breakPointShape, small: breakPointShape }),
    shape({ medium: breakPointShape }),
    shape({ small: breakPointShape }),
  ]),
  onClose: func,
};

PopOver.defaultProps = {
  width: 250,
  open: null,
  label: null,
  position: 'bottom',
  offsetX: 0,
  offsetY: 15,
  arrow: false,
  arrowOffsetY: 15,
  arrowOffsetX: 0,
  breakpoints: null,
  onClose: noop,
};

PopOver.contextType = AppContext;

export default PopOver;
